import { View, Text, colors } from '@fairygodboss/ui';
import Image from 'next/image';
import React, { forwardRef } from 'react';

import { IMAGE_CDN_URL } from '../../../utils/imageCDN';
import { nFormatter } from '../../../utils/TimeFunctions';
import SocialShareBar from '../SocialShareBar/SocialShareBar';

const StarSVG = () => (
    <svg
        width="16"
        height="15"
        viewBox="0 0 16 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <style jsx>
            {`
                svg {
                    position: relative;
                    top: 2px;
                    padding-right: 4px;
                }
            `}
        </style>
        <path
            d="M8 0L10.1678 5.01635L15.6085 5.52786L11.5075 9.13965L12.7023 14.4721L8 11.688L3.29772 14.4721L4.4925 9.13965L0.391548 5.52786L5.83225 5.01635L8 0Z"
            fill="#B394F2"
        />
    </svg>
);

const Socials = ({
    hash, type, contentTitle, canonicalUrl
}) => (
    <View width="147px">
        <style jsx>
            {`
                .component-SocialShareBar {
                    justify-content: start;
                }
                @media screen and (min-width: 600px) {
                    .component-SocialShareBar {
                        justify-content: end;
                    }
                }
                .at-svc-link {
                    margin-right: 0px !important;
                }
                .at-svc-linkedin {
                    margin-left: 0px !important;
                }
            `}
        </style>

        <SocialShareBar
            hash={hash}
            type={type}
            contentTitle={contentTitle}
            canonicalUrl={canonicalUrl}
            location="singleContent"
        />
    </View>
);

export const Author = forwardRef(
    (
        {
            author, contentModifiedDate, hash, type, contentTitle, ...others
        },
        ref
    ) => {
        const {
            image,
            username,
            karmaScore,
            profileCredential,
            authorHash,
            authorName,
            authorImage,
            active,
            isVip,
        } = author;
        let nameToUse = authorName || (active ? username : 'Fairygodboss');
        nameToUse = nameToUse || 'Fairygodboss';
        const publicUrl = authorHash
            ? `/users/profile/${authorHash}/${nameToUse}`
            : null;
        // eslint-disable-next-line no-nested-ternary
        let imageToUse = authorImage?.src
            ? authorImage
            : active
                ? image
                : { src: '/v1/production/tip/9/owner.webp' };
        imageToUse = imageToUse?.src
            ? imageToUse
            : { src: '/v1/avatar_black_160_vtg7lw.svg' };

        return (
            <View ref={ref} display="flex" {...others}>
                <View
                    display="flex"
                    justifyContent="space-between"
                    flexDirection={{ _: 'column', sm: 'row' }}
                    width="100%"
                    alignItems="start"
                    pt={{
                        _: '50px',
                        xs: '20px',
                        sm: '20px',
                        md: '25px',
                        lg: '40px',
                    }}
                >
                    <View display="flex" flexDirection="column">
                        <View display="flex" flex="1">
                            <View
                                as={publicUrl && active ? 'a' : 'div'}
                                href={active ? publicUrl : ''}
                                mr="16px"
                                target="_self"
                                className="author-container"
                            >
                                <style global jsx>
                                    {`
                                        .author-container {
                                            position: relative;
                                        }
                                        .author-image {
                                            height: 48px;
                                            width: 48px;
                                        }
                                        .author-image img {
                                            clip-path: circle(24px);
                                        }
                                        .vip-badge {
                                            width: 16px;
                                            position: absolute;
                                            left: 32px;
                                            top: 33px;
                                        }
                                        @media screen and (max-width: 905px) {
                                            .vip-badge {
                                                left: 11px;
                                                top: 12px;
                                            }
                                        }
                                    `}
                                </style>

                                <Image
                                    className="author-image"
                                    src={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_48/${
                                        imageToUse?.src
                                            ? imageToUse.src
                                            : 'v1/production/tip/9/owner.webp'
                                    }?temp=1`}
                                    alt={imageToUse?.alt || 'Profile Picture'}
                                    width="48"
                                    height="48"
                                    loading="lazy"
                                    style={{
                                        width: '100% !important',
                                        height: 'auto !important',
                                        clipPath: 'circle(24px)',
                                    }}
                                />
                                {isVip && active && (
                                    <View>
                                        <Image
                                            className="vip-badge"
                                            src={`${IMAGE_CDN_URL}/dpr_1,q_auto,c_scale,f_webp,w_16/v1/SVGs/VIP_LARGE.webp?temp=1`}
                                            alt="VIP"
                                            width="16"
                                            height="16"
                                            loading="lazy"
                                        />
                                    </View>
                                )}
                            </View>
                            <View
                                display="flex"
                                flexWrap="wrap"
                                flexDirection="column"
                            >
                                <View
                                    display="flex"
                                    flexDirection={{
                                        _: 'column',
                                        lg: 'row',
                                    }}
                                    alignItems={{
                                        _: 'start',
                                        lg: 'center',
                                    }}
                                    flexWrap="wrap"
                                    // flex="1"
                                >
                                    <View display="flex">
                                        <Text
                                            className="author-name"
                                            fontWeight="700"
                                            margin="0"
                                            textAlign="left"
                                            variant="labelMd"
                                            letterSpacing="-0.4px"
                                            lineHeight="18px"
                                            color={colors.neutral[100]}
                                        >
                                            <style global jsx>
                                                {`
                                                    .author-name {
                                                        font-weight: 700;
                                                        color: ${colors
                .neutral[100]};
                                                    }
                                                `}
                                            </style>
                                            {nameToUse}
                                            {karmaScore && active ? (
                                                <Text
                                                    pr={{
                                                        _: '0',
                                                        lg: '12px',
                                                    }}
                                                    pl={{
                                                        _: '4px',
                                                        md: '12px',
                                                    }}
                                                    color={colors.neutral[100]}
                                                    variant="labelMd"
                                                    fontWeight="400"
                                                >
                                                    <StarSVG />
                                                    {nFormatter(karmaScore, 2)}
                                                </Text>
                                            ) : null}
                                        </Text>
                                    </View>
                                    <Text
                                        pt={{ _: '2px', lg: '0px' }}
                                        letterSpacing="-0.4px"
                                        color={colors.neutral[100]}
                                        variant="labelMd"
                                    >
                                        {active ? profileCredential : ''}
                                    </Text>
                                </View>
                                <View flex="1" mt={{ _: '0px', lg: '4px' }}>
                                    <Text
                                        color={colors.neutral[70]}
                                        variant="labelMd"
                                    >
                                        {contentModifiedDate}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View
                        pl={{ _: '0px', sm: '8px' }}
                        pt={{ _: '16px', sm: '0px' }}
                    >
                        <Socials
                            hash={hash}
                            type={type}
                            contentTitle={contentTitle}
                        />
                    </View>
                </View>
            </View>
        );
    }
);
